import React, { useEffect, useState } from 'react';
import {
  getAdminProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getAdminCategories,
} from '../../services/adminApi';
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
} from 'react-icons/hi';

interface Product {
  id: number;
  category: number;
  category_name?: string;
  name: string;
  slug: string;
  description: string;
  price: string;
  discount_price: string | null;
  image: string;
  image_2: string;
  image_3: string;
  sizes: string[];
  colors: string[];
  in_stock: boolean;
  featured: boolean;
}

interface Category {
  id: number;
  name: string;
}

const emptyProduct = {
  category: 0,
  name: '',
  slug: '',
  description: '',
  price: '',
  discount_price: '',
  image: '',
  image_2: '',
  image_3: '',
  sizes: '',
  colors: '',
  in_stock: true,
  featured: false,
};

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        getAdminProducts(),
        getAdminCategories(),
      ]);
      const prods = Array.isArray(prodRes.data) ? prodRes.data : prodRes.data.results || [];
      const cats = Array.isArray(catRes.data) ? catRes.data : catRes.data.results || [];
      setProducts(prods);
      setCategories(cats);
    } catch {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyProduct, category: categories[0]?.id || 0 });
    setShowModal(true);
    setError('');
  };

  const openEdit = (p: Product) => {
    setEditingId(p.id);
    setForm({
      category: p.category,
      name: p.name,
      slug: p.slug,
      description: p.description,
      price: p.price,
      discount_price: p.discount_price || '',
      image: p.image,
      image_2: p.image_2 || '',
      image_3: p.image_3 || '',
      sizes: Array.isArray(p.sizes) ? p.sizes.join(', ') : '',
      colors: Array.isArray(p.colors) ? p.colors.join(', ') : '',
      in_stock: p.in_stock,
      featured: p.featured,
    });
    setShowModal(true);
    setError('');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await deleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      setMessage('Product deleted');
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setError('Failed to delete product');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload: Record<string, unknown> = {
      ...form,
      sizes: form.sizes
        ? form.sizes.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      colors: form.colors
        ? form.colors.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      discount_price: form.discount_price || null,
    };
    try {
      if (editingId) {
        await updateProduct(editingId, payload);
        setMessage('Product updated');
      } else {
        await createProduct(payload);
        setMessage('Product created');
      }
      setShowModal(false);
      fetchData();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const msgs = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
        setError(msgs);
      } else {
        setError('Failed to save product');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm({ ...form, [name]: (e.target as HTMLInputElement).checked });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  if (loading) return <div className="admin-spinner" />;

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Products</h1>
        <button className="admin-btn admin-btn-primary" onClick={openAdd}>
          <HiOutlinePlus size={18} /> Add Product
        </button>
      </div>

      {message && <div className="admin-alert admin-alert-success">{message}</div>}
      {error && !showModal && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Category</th>
              <th>Price</th>
              <th>In Stock</th>
              <th>Featured</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={7} className="admin-table-empty">No products found</td>
              </tr>
            ) : (
              products.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.image ? (
                      <img src={p.image} alt={p.name} className="admin-table-thumb" />
                    ) : (
                      <div className="admin-table-thumb-placeholder" />
                    )}
                  </td>
                  <td>{p.name}</td>
                  <td>{p.category_name || p.category}</td>
                  <td>
                    {p.discount_price ? (
                      <>
                        <span className="admin-price-old">${p.price}</span>{' '}
                        <span className="admin-price-new">${p.discount_price}</span>
                      </>
                    ) : (
                      <span>${p.price}</span>
                    )}
                  </td>
                  <td>
                    <span className={`admin-badge ${p.in_stock ? 'admin-badge-green' : 'admin-badge-red'}`}>
                      {p.in_stock ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-badge ${p.featured ? 'admin-badge-gold' : 'admin-badge-gray'}`}>
                      {p.featured ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-btn-icon" onClick={() => openEdit(p)} title="Edit">
                        <HiOutlinePencil size={16} />
                      </button>
                      <button className="admin-btn-icon admin-btn-icon-danger" onClick={() => handleDelete(p.id)} title="Delete">
                        <HiOutlineTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingId ? 'Edit Product' : 'Add Product'}</h2>
              <button className="admin-btn-icon" onClick={() => setShowModal(false)}>
                <HiOutlineX size={20} />
              </button>
            </div>
            {error && <div className="admin-alert admin-alert-error">{error}</div>}
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label>Name</label>
                  <input name="name" value={form.name} onChange={handleChange} required />
                </div>
                <div className="admin-form-group">
                  <label>Slug</label>
                  <input name="slug" value={form.slug} onChange={handleChange} required />
                </div>
                <div className="admin-form-group">
                  <label>Category</label>
                  <select name="category" value={form.category} onChange={handleChange} required>
                    <option value="">Select category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Price</label>
                  <input name="price" value={form.price} onChange={handleChange} required type="number" step="0.01" />
                </div>
                <div className="admin-form-group">
                  <label>Discount Price</label>
                  <input name="discount_price" value={form.discount_price} onChange={handleChange} type="number" step="0.01" />
                </div>
                <div className="admin-form-group">
                  <label>Image URL</label>
                  <input name="image" value={form.image} onChange={handleChange} placeholder="/images/product.jpg" />
                </div>
                <div className="admin-form-group">
                  <label>Image 2 URL</label>
                  <input name="image_2" value={form.image_2} onChange={handleChange} placeholder="/images/product-2.jpg" />
                </div>
                <div className="admin-form-group">
                  <label>Image 3 URL</label>
                  <input name="image_3" value={form.image_3} onChange={handleChange} placeholder="/images/product-3.jpg" />
                </div>
                <div className="admin-form-group">
                  <label>Sizes (comma-separated)</label>
                  <input name="sizes" value={form.sizes} onChange={handleChange} placeholder="S, M, L, XL" />
                </div>
                <div className="admin-form-group">
                  <label>Colors (comma-separated)</label>
                  <input name="colors" value={form.colors} onChange={handleChange} placeholder="Black, White, Red" />
                </div>
              </div>
              <div className="admin-form-group">
                <label>Description</label>
                <textarea name="description" value={form.description} onChange={handleChange} rows={3} />
              </div>
              <div className="admin-form-row">
                <label className="admin-checkbox">
                  <input type="checkbox" name="in_stock" checked={form.in_stock} onChange={handleChange} />
                  In Stock
                </label>
                <label className="admin-checkbox">
                  <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} />
                  Featured
                </label>
              </div>
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
